import { Group, Paper, SimpleGrid, Skeleton, Stack, Table } from "@mantine/core"
import type {
  ICardGridSkeletonProps,
  IKpiRowSkeletonProps,
  ILineListSkeletonProps,
  IPageHeaderSkeletonProps,
  ITableSkeletonProps,
} from "./interfaces"

/**
 * Reusable loading-skeleton primitives. Each page-view composes the handful it
 * needs in its `isLoading` branch so the loading state mirrors the populated
 * layout (a held shape) instead of a bare centered spinner. Pure presentation,
 * Mantine `Skeleton` under the hood — fully themeable, no literal colours.
 */

/** Title + subtitle, with an optional trailing action block. */
export const PageHeaderSkeleton = ({ withActions = true }: IPageHeaderSkeletonProps): React.JSX.Element => (
  <Group justify="space-between" align="flex-end" mb="lg" wrap="nowrap">
    <Stack gap={8}>
      <Skeleton height={28} width={220} radius="sm" />
      <Skeleton height={14} width={300} radius="sm" />
    </Stack>
    {withActions && <Skeleton height={36} width={160} radius="sm" />}
  </Group>
)

/** A row of KPI summary cards (label + value). */
export const KpiRowSkeleton = ({ count = 3 }: IKpiRowSkeletonProps): React.JSX.Element => (
  <SimpleGrid cols={{ base: 1, sm: count }} mb="xl">
    {Array.from({ length: count }, (_, i) => (
      <Paper key={i} withBorder p="md">
        <Skeleton height={12} width="50%" radius="sm" mb="sm" />
        <Skeleton height={26} width="70%" radius="sm" />
      </Paper>
    ))}
  </SimpleGrid>
)

/** A header row + `rows` body rows of `cols` cells. */
export const TableSkeleton = ({ rows = 6, cols = 5 }: ITableSkeletonProps): React.JSX.Element => (
  <Paper withBorder>
    <Table>
      <Table.Thead>
        <Table.Tr>
          {Array.from({ length: cols }, (_, i) => (
            <Table.Th key={i}>
              <Skeleton height={12} width="60%" radius="sm" />
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {Array.from({ length: rows }, (_, r) => (
          <Table.Tr key={r}>
            {Array.from({ length: cols }, (_, c) => (
              <Table.Td key={c}>
                <Skeleton height={12} radius="sm" />
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Paper>
)

/** A responsive grid of content cards (meeting list, etc.). */
export const CardGridSkeleton = ({ count = 6 }: ICardGridSkeletonProps): React.JSX.Element => (
  <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
    {Array.from({ length: count }, (_, i) => (
      <Paper key={i} withBorder p="md">
        <Group justify="space-between" mb="sm" wrap="nowrap">
          <Skeleton height={14} width="55%" radius="sm" />
          <Skeleton height={20} width={48} radius="xl" />
        </Group>
        <Skeleton height={10} width="40%" radius="sm" mb="md" />
        <Group gap="xs" mb="md">
          <Skeleton height={22} width={60} radius="xl" />
          <Skeleton height={22} width={72} radius="xl" />
        </Group>
        <Skeleton height={10} width="80%" radius="sm" mb={6} />
        <Skeleton height={10} width="65%" radius="sm" />
      </Paper>
    ))}
  </SimpleGrid>
)

/** Transcript-style rows: a fixed speaker label + a flexible text line. */
export const LineListSkeleton = ({ rows = 10 }: ILineListSkeletonProps): React.JSX.Element => (
  <Stack gap="sm">
    {Array.from({ length: rows }, (_, i) => (
      <Group key={i} gap="sm" align="flex-start" wrap="nowrap">
        <Skeleton height={12} width={88} radius="sm" style={{ flexShrink: 0 }} />
        <Skeleton height={12} radius="sm" style={{ flex: 1 }} />
      </Group>
    ))}
  </Stack>
)

/** Meeting-detail style: a hero block + a grid of summary cards. */
export const DetailSkeleton = (): React.JSX.Element => (
  <Stack gap="lg">
    <Paper withBorder p="lg">
      <Skeleton height={24} width="45%" radius="sm" mb="sm" />
      <Skeleton height={12} width="30%" radius="sm" mb="lg" />
      <Group gap="sm">
        <Skeleton height={28} width={90} radius="xl" />
        <Skeleton height={28} width={90} radius="xl" />
        <Skeleton height={28} width={90} radius="xl" />
      </Group>
    </Paper>
    <SimpleGrid cols={{ base: 1, sm: 2 }}>
      {Array.from({ length: 4 }, (_, i) => (
        <Paper key={i} withBorder p="md">
          <Skeleton height={12} width="40%" radius="sm" mb="sm" />
          <Skeleton height={10} radius="sm" mb={6} />
          <Skeleton height={10} width="85%" radius="sm" mb={6} />
          <Skeleton height={10} width="70%" radius="sm" />
        </Paper>
      ))}
    </SimpleGrid>
  </Stack>
)

/** Settings-style: a vertical tab rail (desktop) + a panel of form fields. */
export const TabbedFormSkeleton = (): React.JSX.Element => (
  <Group align="flex-start" gap="xl" wrap="nowrap">
    <Stack gap="xs" w={200} visibleFrom="sm">
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton key={i} height={32} radius="sm" />
      ))}
    </Stack>
    <Stack gap="lg" style={{ flex: 1 }}>
      <Skeleton height={22} width="40%" radius="sm" />
      {Array.from({ length: 5 }, (_, i) => (
        <Stack key={i} gap={6}>
          <Skeleton height={12} width="25%" radius="sm" />
          <Skeleton height={36} radius="sm" />
        </Stack>
      ))}
    </Stack>
  </Group>
)
